// Invoices and e-invoicing (docs/clairvoyance/06-accounting.md §6.8, 04 §4.6). Server-written:
// `invoices/{inv_…}` is created by the invoicing functions and read by staff within scope. The
// invoice model itself (`@ecobox/einvoice`) is stored as `model` and typed where it is used, because
// the schema package cannot depend on the invoice package.
import { z } from 'zod';
import {
  EINVOICE_COUNTRY_CODES,
  branchCodeSchema,
  einvoicingSchema,
  legalDetailsSchema,
  sectorCodeSchema,
} from './config';
import { trackingNoSchema } from './cargo';
import { defineCommand } from './define';

const minor = z.number().int().nonnegative().max(1e12);
const iso = z.string();

export const INVOICE_ID_PATTERN = /^inv_[0-9A-HJKMNP-TV-Z]{26}$/;
export const invoiceIdSchema = z.string().regex(INVOICE_ID_PATTERN, 'Invoice id like inv_01J…');

export const INVOICE_DOCUMENT_TYPES = [
  'tax_invoice',
  'simplified_tax_invoice',
  'credit_note',
  'debit_note',
] as const;
export const INVOICE_SERIES = ['invoice', 'credit_note', 'debit_note'] as const;

/**
 * Where an invoice is on its way to the tax authority:
 * `issued` (waiting, or waiting to retry) → `submitted` (sent, answer pending) →
 * `reported` (ZATCA reporting) · `cleared` (ZATCA clearance) · `accepted` (UAE ASP), or `rejected`.
 * `not_required` for documents nobody receives (a printed UAE tax invoice, Qatar, India).
 */
export const EINVOICE_STATUSES = [
  'not_required',
  'issued',
  'submitted',
  'reported',
  'cleared',
  'accepted',
  'rejected',
] as const;
export type EInvoiceStatus = (typeof EINVOICE_STATUSES)[number];

export const EINVOICE_CHANNELS = ['zatca_reporting', 'zatca_clearance', 'uae_asp', 'none'] as const;
export type EInvoiceChannel = (typeof EINVOICE_CHANNELS)[number];

/** Statuses after which nothing more is sent. */
export const EINVOICE_FINAL_STATUSES: readonly EInvoiceStatus[] = [
  'not_required',
  'reported',
  'cleared',
  'accepted',
  'rejected',
];

export const invoiceMessageSchema = z
  .object({
    code: z.string().max(80).optional(),
    message: z.string().max(1000),
    severity: z.enum(['info', 'warning', 'error']),
  })
  .strict();
export type InvoiceMessage = z.infer<typeof invoiceMessageSchema>;

export const invoiceEinvoiceSchema = z.object({
  channel: z.enum(EINVOICE_CHANNELS),
  status: z.enum(EINVOICE_STATUSES),
  /** KSA clearance: the invoice is not final (and must not be shared) until ZATCA clears it. */
  final: z.boolean(),
  attempts: z.number().int().nonnegative(),
  nextAttemptAt: iso.optional(),
  /** KSA simplified: issue time plus 24 hours. */
  deadlineAt: iso.optional(),
  submittedAt: iso.optional(),
  completedAt: iso.optional(),
  lastError: z.string().max(1000).optional(),
  /** Why it was rejected, or what the authority warned about. */
  reasons: z.array(invoiceMessageSchema).max(50).default([]),
  aspReference: z.string().max(120).optional(),
  aspProvider: z.string().max(60).optional(),
  exceptionId: z.string().optional(),
  alertId: z.string().optional(),
});
export type InvoiceEinvoice = z.infer<typeof invoiceEinvoiceSchema>;

export const invoiceTotalsSchema = z
  .object({
    taxExclusiveMinor: z.number().int(),
    vatMinor: z.number().int(),
    taxInclusiveMinor: z.number().int(),
    payableMinor: z.number().int(),
  })
  .strict();

/** `invoices/{inv_…}`. */
export const invoiceDocSchema = z.object({
  id: invoiceIdSchema,
  /** The issuing entity (= sector code). */
  entityId: sectorCodeSchema,
  sectorId: sectorCodeSchema,
  branchId: branchCodeSchema,
  /** The shipment's tracking number. */
  shipmentId: trackingNoSchema,
  customerId: z.string().optional(),
  /**
   * Issued by the system test's test company (its sector is `testData`). Only the system test, with
   * its fake tax authority and in-memory files, sends these; the deployed trigger and schedule skip them.
   */
  testData: z.boolean().optional(),
  number: z.string().min(5).max(40),
  series: z.enum(INVOICE_SERIES),
  year: z.number().int(),
  documentType: z.enum(INVOICE_DOCUMENT_TYPES),
  country: z.enum(EINVOICE_COUNTRY_CODES),
  profile: z.string().max(40),
  mode: z.enum(['simulation', 'production']),
  currency: z.string().length(3),
  issuedAt: iso,
  issueDate: z.string(),
  issueTime: z.string(),
  buyerName: z.string().max(200),
  buyerKind: z.enum(['individual', 'business']),
  totals: invoiceTotalsSchema,
  /** The VAT-inclusive total, for lists and sorting (negative documents are never stored: a note has its own type). */
  grossMinor: minor,
  /** Credit notes issued against this invoice, in total. */
  creditedMinor: minor.default(0),
  /** Debit notes issued against this invoice, in total (an amendment that added money). */
  debitedMinor: minor.default(0),
  /** Of `creditedMinor`, what finance credited by hand with `issueCreditNote`: the shipment does not know it. */
  manualCreditedMinor: minor.default(0),
  /** The same by line of the invoice (`lineId` → minor units), so a line is never credited twice. */
  creditedByLine: z.record(z.string(), minor).default({}),
  originalInvoiceId: invoiceIdSchema.optional(),
  originalNumber: z.string().optional(),
  reason: z.string().max(300).optional(),
  uuid: z.string(),
  qr: z.string().optional(),
  invoiceHash: z.string().optional(),
  icv: z.number().int().positive().optional(),
  previousInvoiceHash: z.string().optional(),
  egsUnitId: z.string().optional(),
  /** The `@ecobox/einvoice` InvoiceModel, as issued. */
  model: z.record(z.string(), z.unknown()),
  warnings: z.array(invoiceMessageSchema).max(50).default([]),
  /** The collection journal this invoice documents (it was posted by the sale, not by the invoice). */
  journalIds: z.array(z.string()).default([]),
  creditJournalId: z.string().optional(),
  storage: z.object({
    status: z.enum(['pending', 'stored']),
    xmlPath: z.string().optional(),
    printPath: z.string(),
    textPath: z.string(),
    /** KSA clearance: ZATCA's cleared copy, which is the one to share. */
    clearedXmlPath: z.string().optional(),
  }),
  einvoice: invoiceEinvoiceSchema,
  commandId: z.string().optional(),
  issuedBy: z.string(),
  createdAt: iso,
  updatedAt: iso,
});
export type InvoiceDoc = z.infer<typeof invoiceDocSchema>;

export function isCreditNoteDoc(doc: Pick<InvoiceDoc, 'documentType'>): boolean {
  return doc.documentType === 'credit_note' || doc.documentType === 'debit_note';
}

// ---- Commands (04 §4.6) ------------------------------------------------------------

/**
 * Issue (or re-issue after a blocked attempt, or late) the invoice of a shipment. The normal path
 * needs no command: `collectCargo`, `completePickup` and `recordPayment` issue it per the country's
 * `issueOn`.
 */
export const issueInvoice = defineCommand({
  type: 'issueInvoice',
  permission: 'finance.payment.record',
  alsoPermitted: ['finance.invoice.credit'],
  principals: [],
  offline: false,
  payload: z
    .object({
      shipmentId: trackingNoSchema,
    })
    .strict(),
});

/** A credit note against an issued invoice: all of it, or the lines given (VAT-inclusive amounts). */
export const issueCreditNote = defineCommand({
  type: 'issueCreditNote',
  permission: 'finance.invoice.credit',
  principals: [],
  offline: false,
  payload: z
    .object({
      invoiceId: invoiceIdSchema,
      reason: z.string().trim().min(3).max(300),
      /** Omit to credit the whole invoice. `lineId` is the `id` of a line of the original invoice. */
      lines: z
        .array(
          z
            .object({
              lineId: z.string().min(1).max(20),
              grossMinor: z.number().int().positive().max(1e12),
            })
            .strict(),
        )
        .min(1)
        .max(50)
        .optional(),
    })
    .strict(),
});

export const INVOICING_COMMANDS = { issueInvoice, issueCreditNote } as const;

// ---- Callables ---------------------------------------------------------------------

export const ZATCA_ENVIRONMENTS = ['sandbox', 'simulation', 'production'] as const;

/** `einvoiceOnboard`: owner only. The OTP is typed once and never stored. */
export const einvoiceOnboardInputSchema = z
  .object({
    entityId: sectorCodeSchema,
    otp: z.string().regex(/^\d{4,10}$/, 'The OTP from the Fatoora portal is 4 to 10 digits'),
    environment: z.enum(ZATCA_ENVIRONMENTS),
  })
  .strict();
export type EinvoiceOnboardInput = z.infer<typeof einvoiceOnboardInputSchema>;

export interface OnboardStepResult {
  id:
    | 'settings'
    | 'keys'
    | 'csr'
    | 'compliance_csid'
    | 'compliance_checks'
    | 'production_csid'
    | 'store';
  label: string;
  ok: boolean;
  detail: string;
}

export interface EinvoiceOnboardResult {
  ok: boolean;
  environment: (typeof ZATCA_ENVIRONMENTS)[number];
  steps: OnboardStepResult[];
  csid?: { requestId: string; expiresAt?: string; onboardedAt: string };
}

/** `saveEinvoiceSettings`: the legal details and e-invoicing settings of one entity. */
export const saveEinvoiceSettingsInputSchema = z
  .object({
    sectorId: sectorCodeSchema,
    legal: legalDetailsSchema,
    einvoicing: einvoicingSchema,
    /** Optimistic concurrency: the `docVersion` the editor saw. */
    expectedVersion: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SaveEinvoiceSettingsInput = z.infer<typeof saveEinvoiceSettingsInputSchema>;
